import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { BarChart3, Download, AlertTriangle, Clock, ShieldAlert, RefreshCw, Info } from "lucide-react";
import { toast } from "sonner";

const PLATFORMS = [["", "All platforms"], ["shopee", "Shopee"], ["tiktok", "TikTok Shop"]];
const STATUS = [["", "All statuses"], ["unpaid", "Unpaid"], ["to_ship", "To ship"], ["shipping", "Shipping"], ["completed", "Completed"], ["cancelled", "Cancelled"], ["return_refund", "Return / Refund"], ["other", "Other"]];
const BUCKET_LABEL = { unpaid: "Unpaid", to_ship: "To ship", shipping: "Shipping", completed: "Completed", cancelled: "Cancelled", return_refund: "Return/Refund", other: "Other", total: "Total" };

const todayMYT = () => new Date(Date.now() + 8 * 3600e3).toISOString().slice(0, 10);
const daysAgoMYT = (n) => new Date(Date.now() + 8 * 3600e3 - n * 86400e3).toISOString().slice(0, 10);

export default function MarketplaceReports() {
  const [platform, setPlatform] = useState("");
  const [shopId, setShopId] = useState("");
  const [start, setStart] = useState(daysAgoMYT(30));
  const [end, setEnd] = useState(todayMYT());
  const [status, setStatus] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const params = useCallback(() => new URLSearchParams({ platform, shop_id: shopId, start_date: start, end_date: end, status }).toString(), [platform, shopId, start, end, status]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/marketplace/reports?${params()}`);
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [params]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [platform, shopId, start, end, status]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const r = await api.get(`/marketplace/reports/export?${params()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `marketplace_orders_${start}_${end}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Orders CSV exported (filtered rows)");
    } catch (e) {
      const d = e.response?.status === 403 ? "You do not have export permission." : formatApiError(e.response?.data?.detail);
      toast.error(d);
    } finally { setExporting(false); }
  };

  const stores = (data?.stores || []).filter((s) => !platform || s.platform === platform);
  const os = data?.order_summary || {};

  return (
    <div className="space-y-4" data-testid="marketplace-reports-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-400">Marketplace</div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><BarChart3 className="w-6 h-6 text-indigo-500" />Reports</h1>
          <p className="text-sm text-slate-500 mt-0.5">Read-only. MYT (UTC+8) calendar days · currencies kept separate · amounts counted only where the source provides them.</p>
        </div>
        <Button data-testid="report-export-btn" onClick={exportCsv} disabled={exporting} className="h-9 bg-slate-900 hover:bg-slate-800">
          <Download className="w-4 h-4 mr-1.5" />Export orders CSV
        </Button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 grid sm:grid-cols-5 gap-3">
        <Field label="Platform"><select data-testid="rep-platform" value={platform} onChange={(e) => { setPlatform(e.target.value); setShopId(""); }} className="w-full h-9 rounded-md border border-slate-200 text-sm px-2">{PLATFORMS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
        <Field label="Store"><select data-testid="rep-store" value={shopId} onChange={(e) => setShopId(e.target.value)} className="w-full h-9 rounded-md border border-slate-200 text-sm px-2"><option value="">All connected</option>{stores.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}</select></Field>
        <Field label="From (MYT)"><Input data-testid="rep-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-9" /></Field>
        <Field label="To (MYT)"><Input data-testid="rep-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9" /></Field>
        <Field label="Status"><select data-testid="rep-status" value={status} onChange={(e) => setStatus(e.target.value)} className="w-full h-9 rounded-md border border-slate-200 text-sm px-2">{STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
      </div>

      {loading ? <div className="p-8 text-center text-slate-400">Loading…</div> : data && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="order-summary">
            {["total", ...["unpaid", "to_ship", "shipping", "completed", "cancelled", "return_refund", "other"]].map((b) => (
              <div key={b} className={`rounded-lg border p-3 ${b === "total" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white"}`} data-testid={`sum-${b}`}>
                <div className={`text-xl font-bold ${b === "total" ? "" : "text-slate-800"}`}>{os[b] ?? 0}</div>
                <div className={`text-[11px] ${b === "total" ? "text-slate-300" : "text-slate-500"}`}>{BUCKET_LABEL[b]}</div>
              </div>
            ))}
          </div>

          <Panel title="Sales by currency" testid="sales-by-currency" hint="Ordered gross only (what buyers ordered). Currencies never combined. Cancelled shown separately.">
            {(data.sales_by_currency || []).length === 0 ? <Empty>No orders in range</Empty> : (
              <table className="w-full text-sm"><thead><tr className="text-left text-[11px] text-slate-400 border-b"><th className="py-1.5">Currency</th><th>Orders</th><th>Ordered gross</th><th>Cancelled gross</th><th>Amount coverage</th></tr></thead>
                <tbody>{data.sales_by_currency.map((c) => (
                  <tr key={c.currency} className="border-b last:border-0" data-testid={`sales-${c.currency}`}>
                    <td className="py-1.5 font-mono font-semibold">{c.currency}</td><td>{c.orders}</td>
                    <td className="font-semibold">{c.ordered_gross.toFixed(2)}</td>
                    <td className="text-rose-600">{c.cancelled_gross.toFixed(2)}</td>
                    <td>{Math.round((c.gross_coverage || 0) * 100)}%</td>
                  </tr>))}</tbody></table>
            )}
          </Panel>

          <Panel title="Settlement & profit" testid="financials-unavailable" hint="No finance/settlement/refund feed is connected — reported as unavailable, never 0.">
            <div className="flex flex-wrap gap-2">
              {Object.keys(data.financials_unavailable || {}).map((k) => (
                <Badge key={k} variant="outline" className="text-[11px] border-amber-300 text-amber-800 bg-amber-50" data-testid={`fin-${k}`}>{k.replace("_", " ")}: unavailable</Badge>
              ))}
            </div>
          </Panel>

          <div className="grid lg:grid-cols-2 gap-4">
            <Panel title="Ship-by exceptions" testid="ship-by" hint="Overdue = ship_by before today (MYT) and still unpaid/to-ship. Only where source provides ship_by.">
              <div className="flex items-center gap-2 text-sm"><Clock className="w-4 h-4 text-amber-500" /><span className="font-bold text-lg">{data.ship_by_exceptions.overdue_count}</span> overdue</div>
              <div className="text-[11px] text-slate-400 mt-1">Coverage: {data.ship_by_exceptions.coverage.orders_with_ship_by}/{data.ship_by_exceptions.coverage.orders} orders carry a ship-by date</div>
            </Panel>
            <Panel title="SKU mapping health" testid="mapping-health" hint="Resolved via the per-store SKU mapping — no guessed links.">
              {data.mapping_health.distinct_order_skus === 0 ? <Empty>No order SKUs in range</Empty> : (
                <div className="flex flex-wrap gap-2 text-[11px]">
                  {Object.entries(data.mapping_health.by_state).map(([s, n]) => <Badge key={s} variant="outline" className="capitalize">{s}: {n}</Badge>)}
                  <Badge className="bg-emerald-600">Mapped coverage {Math.round((data.mapping_health.mapped_coverage || 0) * 100)}%</Badge>
                </div>
              )}
            </Panel>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <Panel title="Product summary" testid="product-summary" hint="Imported listings for connected stores.">
              <div className="text-sm">Total listings: <span className="font-bold">{data.product_summary.total}</span></div>
              <div className="flex flex-wrap gap-2 mt-1 text-[11px]">{Object.entries(data.product_summary.by_status || {}).map(([s, n]) => <Badge key={s} variant="outline" className="capitalize">{s.replace("_", " ")}: {n}</Badge>)}</div>
            </Panel>
            <Panel title="Sync freshness & coverage" testid="sync-health" hint="Latest run per connected store; fresh ≤ 24h.">
              <div className="text-[11px] text-slate-500 mb-1">Stores synced {data.sync_health.store_coverage.stores_ever_synced}/{data.sync_health.store_coverage.connected_stores} · failures {data.sync_health.sync_failures}</div>
              {[...data.sync_health.orders.map(r => ({ ...r, kind: "orders" })), ...data.sync_health.listings.map(r => ({ ...r, kind: "listings" }))].length === 0 ? <Empty>No sync runs yet</Empty> :
                [...data.sync_health.orders.map(r => ({ ...r, kind: "orders" })), ...data.sync_health.listings.map(r => ({ ...r, kind: "listings" }))].map((r, i) => (
                  <div key={i} className="flex items-center gap-2 text-[11px] py-0.5" data-testid={`sync-${r.kind}-${r.shop_id}`}>
                    {r.fresh ? <RefreshCw className="w-3 h-3 text-emerald-500" /> : <AlertTriangle className="w-3 h-3 text-amber-500" />}
                    <span className="capitalize">{r.kind}</span>·<span className="capitalize">{r.platform}</span>·<span className="capitalize">{r.status}</span>
                    <span className="text-slate-400">{r.age_hours == null ? "—" : `${r.age_hours}h ago`}{r.errors ? ` · ${r.errors} err` : ""}</span>
                  </div>))}
            </Panel>
          </div>

          <Panel title="Listing drafts & blocked commands" testid="draft-health" hint="Validation + guarded transport (Phase 2).">
            <div className="flex flex-wrap gap-2 text-[11px]">
              <Badge variant="outline">Total: {data.draft_health.total}</Badge>
              <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50">Ready: {data.draft_health.ready}</Badge>
              <Badge variant="outline" className="border-amber-300 text-amber-800 bg-amber-50">Needs fix: {data.draft_health.needs_fix}</Badge>
              <Badge variant="outline">Validation errors: {data.draft_health.total_validation_errors}</Badge>
              <Badge variant="outline">Reqs unverified: {data.draft_health.requirements_unverified}</Badge>
              <Badge variant="outline" className="border-indigo-200 text-indigo-700"><ShieldAlert className="w-3 h-3 mr-1" />Publish blocked: {data.draft_health.publish_commands_blocked}</Badge>
            </div>
          </Panel>

          <details className="rounded-xl border border-slate-200 bg-white p-4" data-testid="metrics-catalog">
            <summary className="cursor-pointer text-sm font-semibold flex items-center gap-2"><Info className="w-4 h-4 text-slate-400" />Metric definitions — source, timestamp basis & coverage</summary>
            <div className="overflow-x-auto mt-3"><table className="w-full text-[11px]"><thead><tr className="text-left text-slate-400 border-b"><th className="py-1">Metric</th><th>Source</th><th>Timestamp basis</th><th>Coverage</th></tr></thead>
              <tbody>{data.metrics_catalog.map((m, i) => (<tr key={i} className="border-b last:border-0 align-top"><td className="py-1 font-medium">{m.metric}</td><td className="text-slate-500">{m.source}</td><td className="text-slate-500">{m.timestamp_basis}</td><td className="text-slate-500">{m.coverage}</td></tr>))}</tbody></table></div>
          </details>
          <div className="text-[11px] text-slate-400">Window: {data.window.start_date || "—"} → {data.window.end_date || "—"} · {data.window.timezone} · generated {new Date(data.window.generated_at).toLocaleString()}</div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (<div><label className="text-xs text-slate-500 block mb-1">{label}</label>{children}</div>);
const Empty = ({ children }) => (<div className="text-xs text-slate-400 py-2">{children}</div>);
const Panel = ({ title, hint, testid, children }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
    <div className="flex items-center justify-between"><h3 className="text-sm font-semibold">{title}</h3>{hint && <span className="text-[10px] text-slate-400 max-w-[55%] text-right">{hint}</span>}</div>
    <div className="mt-2">{children}</div>
  </div>
);
